import React, { useState } from 'react';
import { formatMoney } from './cartAnalyticsFormat';

// Dependency-free bar chart (inline SVG): the admin has no chart library and this one series is enough.
const METRICS = [
  ['atc_events', 'Add to cart', false],
  ['carts', 'Carts', false],
  ['items_qty', 'Items', false],
  ['cart_value', 'Cart value', true],
  ['open_value', 'Open cart value', true],
];

const W = 900;
const H = 240;
const PAD = { top: 20, right: 20, bottom: 36, left: 68 };

export default function TrendChart({ rows, ccy, period }) {
  const [metric, setMetric] = useState('atc_events');
  const [, label, isMoney] = METRICS.find((m) => m[0] === metric);
  const data = (rows || []).filter((r) => !ccy || r.ccy === ccy);

  const max = Math.max(1, ...data.map((r) => Number(r[metric]) || 0));
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const step = data.length ? innerW / data.length : innerW;
  const barW = Math.max(2, Math.min(40, step * 0.7));
  const every = Math.max(1, Math.ceil(data.length / 12));

  return (
    <div className="card shadow-sm mb-3 ca-chart-card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="ca-chart-title mb-0">Trend by {period}</h6>
          <select className="form-control form-control-sm ca-metric-select" value={metric} onChange={(e) => setMetric(e.target.value)}>
            {METRICS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        {data.length === 0 ? (
          <div className="text-muted text-center py-4">No data for this range</div>
        ) : (
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={`${label} by ${period}`}>
            {/* Y-axis guide lines at 0%, 50%, 100% */}
            {[0, 0.5, 1].map((frac) => {
              const gy = H - PAD.bottom - frac * innerH;
              const gv = frac * max;
              return (
                <g key={frac}>
                  <line x1={PAD.left} x2={W - PAD.right} y1={gy} y2={gy} stroke={frac === 0 ? '#c8cdd4' : '#e8eaed'} strokeWidth={frac === 0 ? 1 : 0.8} />
                  <text x={PAD.left - 6} y={gy + 4} fontSize="10" textAnchor="end" fill="#9aa3af">
                    {isMoney ? formatMoney(gv, ccy) : gv.toLocaleString('en-US')}
                  </text>
                </g>
              );
            })}
            {data.map((r, i) => {
              const v = Number(r[metric]) || 0;
              const h = (v / max) * innerH;
              const x = PAD.left + i * step + (step - barW) / 2;
              const y = H - PAD.bottom - h;
              return (
                <g key={r.bucket}>
                  <rect x={x} y={y} width={barW} height={Math.max(h, v > 0 ? 1 : 0)} fill="#2f6fed" rx="2" opacity="0.85">
                    <title>{`${r.bucket}: ${isMoney ? formatMoney(v, r.ccy) : v.toLocaleString('en-US')}`}</title>
                  </rect>
                  {i % every === 0 && (
                    <text x={x + barW / 2} y={H - PAD.bottom + 15} fontSize="9.5" textAnchor="middle" fill="#8a93a2">{r.bucket}</text>
                  )}
                </g>
              );
            })}
          </svg>
        )}
      </div>
    </div>
  );
}
